import {
  FulfillmentTypeListResponseType,
  BuyerProductGetResponseChildType,
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { isIneligibleForFulfillmentType, pluralize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { useFulfillmentTypeEligibility } from "../../hooks/useFulfillmentTypeEligibility";
import { useFulfillmentTypeAutoSelect } from "../../hooks/useFulfillmentTypeAutoSelect";
import { CheckoutSelectedFulfillmentType } from "../../pages";
import { Icon } from "@sellar-io/components";

import { CollectionOptionsCard } from "./CollectionOptionsCard";
import {
  CourierDeliveryOptionsCard,
  DeliveryOrderItemType,
} from "./CourierDeliveryOptionsCard";
import { DirectDeliveryOptionsCard } from "./DirectDeliveryOptionsCard";
import { HaulierDeliveryOptionsCard } from "./HaulierDeliveryOptionsCard";
import { useIneligibleForCourierStore } from "../../hooks/useIneligibleForCourierStore";
import { useBasketContext } from "../../hooks";
import { useEffect } from "react";
import { useFulfillmentTypeList } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { HaulierTierInfoCard } from "../../pages/stockList/Components/HaulierTierInfoCard";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { DistributionDeliveryOptionsCard } from "./DistributionDeliveryOptionsCard";
import { useStorefrontBuyerPostcode } from "../../pages/stockList/categoryPage/Storefront/lib/useStorefrontBuyerPostcode";

export const SelectedFulfillmentTypeCardLegacy = ({
  fulfillmentType,
  supplier,
  selectedFulfillmentType,
  supplierStorefront,
  connection,
  onClick,
  orderItems,
  changeFulfillmentType,
  productItem,
}: {
  selectedFulfillmentType?: CheckoutSelectedFulfillmentType;
  fulfillmentType?:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  supplier: SupplierGetResponseType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  connection?:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  orderItems?: DeliveryOrderItemType;
  onClick?: () => void;
  changeFulfillmentType?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  productItem?: Pick<
    BuyerProductGetResponseChildType,
    "containerType" | "unit" | "volume"
  >;
}) => {
  const sdDeliveryArea = supplierStorefront.sdDeliveryArea;
  const directDeliveryArea = supplierStorefront.directDeliveryArea;
  const hasItemIneligibleForCourier = useIneligibleForCourierStore(
    (state) => state.hasItemIneligibleForCourier,
  );
  const { buyerPostcode } = useStorefrontBuyerPostcode();

  const [{ order }] = useBasketContext();

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const {
    isEligibleForCollection,
    isEligibleForCourier,
    isEligibleForDirect,
    isEligibleForDistribution,
    isEligibleForHaulier,
  } = useFulfillmentTypeEligibility({
    allowCollectionOnCheckout: supplier.allowCollectionOnCheckout,
    connection,
    directDeliveryArea,
    sdDeliveryArea,
    eligibleForHaulierOverride:
      hasItemIneligibleForCourier && palletOverrideEnabled,
  });

  const autoSelectedFulfillmentType = useFulfillmentTypeAutoSelect({
    isEligibleForDistribution,
    isEligibleForDirect,
    value: selectedFulfillmentType,
    supplierId: supplier.id,
    onUpdate: (value) => changeFulfillmentType?.(value),
  });

  const resolvedSelectedFulfillmentType =
    autoSelectedFulfillmentType ?? selectedFulfillmentType;

  const shouldFetchCourierOrHaulierFulfillmentType =
    resolvedSelectedFulfillmentType === "courier" ||
    resolvedSelectedFulfillmentType === "haulier";

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled:
        palletOverrideEnabled &&
        shouldFetchCourierOrHaulierFulfillmentType &&
        resolvedSelectedFulfillmentType === "courier",
    },
  );

  const courierFulfillmentType =
    fulfillmentTypeCourierQuery.data?.pages[0]?.[0];

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled:
        palletOverrideEnabled && shouldFetchCourierOrHaulierFulfillmentType,
    },
  );

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const calculateOtherAvailableOptions = (availableOptions: boolean[]) => {
    return availableOptions.filter(Boolean).length;
  };

  useEffect(() => {
    const forceHaulier =
      resolvedSelectedFulfillmentType === "courier" &&
      hasItemIneligibleForCourier &&
      isEligibleForHaulier &&
      palletOverrideEnabled;

    if (forceHaulier) {
      changeFulfillmentType?.("haulier");
    }
  }, [
    resolvedSelectedFulfillmentType,
    hasItemIneligibleForCourier,
    isEligibleForHaulier,
    palletOverrideEnabled,
    changeFulfillmentType,
  ]);

  const orderHasIneligibleItem =
    (order || productItem) && palletOverrideEnabled
      ? [
          ...(order?.OrderItems ?? []),
          ...(productItem ? [{ Product: productItem }] : []),
        ].some((item) => {
          return isIneligibleForFulfillmentType({
            fulfillmentType: courierFulfillmentType,
            item: item.Product,
          });
        })
      : false;

  const localIneligibleState = orderHasIneligibleItem;

  // Keep shared basket eligibility in sync with the derived order state.
  useEffect(() => {
    if (!((order || productItem) && palletOverrideEnabled)) return;
    if (productItem) return;

    useIneligibleForCourierStore.setState({
      hasItemIneligibleForCourier: orderHasIneligibleItem,
    });
  }, [order, productItem, palletOverrideEnabled, orderHasIneligibleItem]);

  if (selectedFulfillmentType === "direct") {
    const otherAvailableOptionsCount = calculateOtherAvailableOptions([
      isEligibleForCollection,
      isEligibleForDistribution,
      isEligibleForCourier,
      isEligibleForHaulier,
    ]);
    return (
      <>
        <DirectDeliveryOptionsCard
          directFulfillmentType={fulfillmentType}
          supplier={supplier}
          deliveryArea={directDeliveryArea}
          orderItems={orderItems}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentType === "sellarDistribution") {
    const otherAvailableOptionsCount = calculateOtherAvailableOptions([
      isEligibleForCollection,
      isEligibleForDirect,
      isEligibleForCourier,
      isEligibleForHaulier,
    ]);
    return (
      <>
        <DistributionDeliveryOptionsCard
          directFulfillmentType={fulfillmentType}
          supplier={supplier}
          deliveryArea={sdDeliveryArea}
          orderItems={orderItems}
          buyerPostcode={buyerPostcode}
        />

        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (
    selectedFulfillmentType === "courier" &&
    (!productItem || !localIneligibleState)
  ) {
    const otherAvailableOptionsCount = calculateOtherAvailableOptions([
      isEligibleForCollection,
      isEligibleForDirect,
      isEligibleForHaulier,
      isEligibleForDistribution,
    ]);
    return (
      <>
        <CourierDeliveryOptionsCard
          courierFulfillmentType={fulfillmentType}
          supplier={supplier}
          orderItems={orderItems}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (
    selectedFulfillmentType === "haulier" ||
    (productItem && localIneligibleState)
  ) {
    const otherAvailableOptionsCount = calculateOtherAvailableOptions([
      isEligibleForCollection,
      isEligibleForDirect,
      isEligibleForCourier,
      isEligibleForDistribution,
    ]);
    return (
      <>
        <HaulierDeliveryOptionsCard
          haulierFulfillmentType={fulfillmentType}
          supplierId={supplier.id}
        />
        {palletOverrideEnabled && (
          <HaulierTierInfoCard
            fulfillmentType={haulierFulfillmentType}
            compact={true}
            supplierStorefront={supplierStorefront}
          />
        )}
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentType === "collection") {
    const otherAvailableOptionsCount = calculateOtherAvailableOptions([
      isEligibleForDirect,
      isEligibleForCourier,
      isEligibleForHaulier,
    ]);
    return (
      <>
        <CollectionOptionsCard supplier={supplier} orderItems={orderItems} />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  return null;
};

const OtherOptionsAvailableButton = ({
  otherAvailableOptionsCount,
  onClick,
}: {
  otherAvailableOptionsCount: number;
  onClick?: () => void;
}) => {
  return (
    <div
      className={twMerge(
        "mt-3 flex flex-row justify-between rounded-lg border border-zinc-300 bg-zinc-100 p-2 text-zinc-800",
        onClick && "cursor-pointer",
      )}
      onClick={() => onClick?.()}
    >
      <p>
        {" "}
        +{otherAvailableOptionsCount} more delivery{" "}
        {pluralize("option", otherAvailableOptionsCount)}
      </p>
      {onClick && (
        <p className={"m-0 flex flex-row items-center gap-2 p-0 text-blue-800"}>
          <span className={"font-semibold"}>SWITCH</span>
          <Icon icon={"SYNC"} />
        </p>
      )}
    </div>
  );
};
